import React, { forwardRef, useState, useRef } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../assets/styles/datepicker-custom.css';
import { registerLocale } from 'react-datepicker';
import ptBR from 'date-fns/locale/pt-BR';
import { FaCalendarAlt, FaTimes } from 'react-icons/fa';

registerLocale('pt-BR', ptBR);

const applyMonthMask = (value) => {
    let cleaned = value.replace(/\D/g, '');

    if (cleaned.length > 6) {
        cleaned = cleaned.substring(0, 6);
    }

    let formatted = '';
    if (cleaned.length > 0) {
        formatted = cleaned.substring(0, 2);
        if (cleaned.length > 2) {
            formatted += '/' + cleaned.substring(2, 6);
        }
    }

    return formatted;
};

const parseInputToMonth = (inputValue) => {
    if (!inputValue) return null;

    const parts = inputValue.split('/');
    if (parts.length !== 2) return null;

    const month = parseInt(parts[0], 10);
    const year = parseInt(parts[1], 10);

    if (isNaN(month) || isNaN(year)) return null;
    if (month < 1 || month > 12) return null;
    if (year < 1900 || year > 2100) return null;
    if (parts[1].length !== 4) return null;

    return new Date(year, month - 1, 1);
};

const CustomInput = forwardRef(({
    value,
    placeholder,
    className,
    onInputChange,
    onCalendarClick,
    isClearable,
    onClear
}, ref) => {
    const [inputValue, setInputValue] = useState(value || '');

    React.useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    const handleChange = (e) => {
        const rawValue = e.target.value;
        const masked = applyMonthMask(rawValue);
        setInputValue(masked);

        if (onInputChange) {
            onInputChange(masked);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
        }
    };

    const handleClear = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setInputValue('');
        if (onClear) {
            onClear();
        }
    };

    return (
        <div className="input-group">
            <input
                className={className}
                placeholder={placeholder}
                value={inputValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                ref={ref}
                maxLength={7}
                autoComplete="off"
            />
            {isClearable && inputValue && (
                <button
                    type="button"
                    className="btn btn-clear"
                    onClick={handleClear}
                    title="Limpar"
                >
                    <FaTimes />
                </button>
            )}
            <button
                type="button"
                className="btn btn-calendar"
                onClick={onCalendarClick}
                title="Abrir calendário"
            >
                <FaCalendarAlt />
            </button>
        </div>
    );
});

const MonthPicker = ({
    selected,
    onChange,
    placeholderText = "mm/aaaa",
    className = "form-control",
    isClearable = true,
    locale = "pt-BR",
    ...props
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const datePickerRef = useRef(null);

    const handleInputChange = (maskedValue) => {
        const parsedDate = parseInputToMonth(maskedValue);
        if (parsedDate) {
            onChange(parsedDate);
        } else if (maskedValue === '') {
            onChange(null);
        }
    };

    const handleCalendarClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(!isOpen);
    };

    const handleCalendarClose = () => {
        setIsOpen(false);
    };

    const handleDateChange = (date) => {
        onChange(date);
        setIsOpen(false);
    };

    const handleClear = () => {
        onChange(null);
    };

    return (
        <ReactDatePicker
            ref={datePickerRef}
            selected={selected}
            onChange={handleDateChange}
            dateFormat="MM/yyyy"
            showMonthYearPicker
            locale={locale}
            placeholderText={placeholderText}
            isClearable={false}
            open={isOpen}
            onClickOutside={handleCalendarClose}
            customInput={
                <CustomInput
                    className={className}
                    placeholder={placeholderText}
                    onInputChange={handleInputChange}
                    onCalendarClick={handleCalendarClick}
                    isClearable={isClearable}
                    onClear={handleClear}
                />
            }
            {...props}
        />
    );
};

export default MonthPicker;
